<template>
  <div class="asset-inventory-edit-container">
    <ds-page-header :title="$t('assetInventoryEdit.page.title')" :subtitle="$t('assetInventoryEdit.page.subtitle')">
      <template #extra>
        <el-button size="small" icon="el-icon-back" @click="_goBack">{{ $t('common.back') }}</el-button>
        <el-button type="primary" size="small" icon="el-icon-check" @click="_doAssetInventory">
          {{ $t('common.submit') }}
        </el-button>
      </template>
    </ds-page-header>

    <div class="ds-panel ds-panel--padded">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('assetInventoryEdit.inventoryInfo') }}</span>
      </div>
      <div class="ds-info-grid">
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryEdit.inventoryName') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryEditInfo.name || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryEdit.inventoryPerson') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryEditInfo.staffName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryEdit.warehouse') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryEditInfo.shName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('assetInventoryEdit.inventoryDate') }}</span>
          <span class="ds-info-grid__value ds-num">{{ assetInventoryEditInfo.invTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item ds-info-grid__item--wide">
          <span class="ds-info-grid__label">{{ $t('assetInventoryEdit.remark') }}</span>
          <span class="ds-info-grid__value">{{ assetInventoryEditInfo.remark || '-' }}</span>
        </div>
      </div>
    </div>

    <div class="ds-panel">
      <ds-toolbar :show-column-setting="false" :show-density="false" :show-refresh="false">
        <template #left><span class="ds-panel__title">{{ $t('assetInventoryEdit.inventoryProducts') }}</span></template>
        <template #right>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="_openChooseProductModal">
            {{ $t('common.select') }}{{ $t('assetInventoryEdit.products') }}
          </el-button>
        </template>
      </ds-toolbar>
      <ds-data-table :data="assetInventoryEditInfo.resourceStores" border
        :empty-description="$t('assetInventoryEdit.page.empty')">
        <el-table-column :label="$t('assetInventoryEdit.productType')" align="center" min-width="140">
          <template slot-scope="scope">{{ scope.row.parentRstName || '-' }} &gt; {{ scope.row.rstName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="resName" :label="$t('assetInventoryEdit.productName')" align="center" min-width="140" />
        <el-table-column :label="$t('assetInventoryEdit.specification')" align="center" width="110">
          <template slot-scope="scope">{{ scope.row.rssName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="resCode" :label="$t('assetInventoryEdit.productCode')" align="center" width="120">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.resCode || '-' }}</span></template>
        </el-table-column>
        <el-table-column prop="isFixedName" :label="$t('assetInventoryEdit.isFixed')" align="center" width="110" />
        <el-table-column :label="$t('assetInventoryEdit.referencePrice')" align="center" min-width="140">
          <template slot-scope="scope">
            <el-select v-model="scope.row.timesId" @change="_changeTimesId($event, scope.$index)" style="width: 100%">
              <el-option :value="''" :label="$t('assetInventoryEdit.selectPrice')" />
              <el-option v-for="time in scope.row.times" :key="time.timesId" :value="time.timesId" :label="String(time.price)" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column :label="$t('assetInventoryEdit.stock')" align="center" width="110">
          <template slot-scope="scope"><span class="ds-num">{{ _getTimesStock(scope.row) }}{{ scope.row.unitCodeName }}</span></template>
        </el-table-column>
        <el-table-column :label="$t('assetInventoryEdit.afterInventoryQty')" align="center" min-width="160">
          <template slot-scope="scope">
            <el-input-number v-model="scope.row.quantity" :placeholder="$t('assetInventoryEdit.requiredQty')"
              controls-position="right" :min="1" style="width: 120px" />
            <span style="margin-left: 5px">{{ scope.row.unitCodeName }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('assetInventoryEdit.remark')" align="center" min-width="140">
          <template slot-scope="scope"><el-input v-model="scope.row.remark" :placeholder="$t('assetInventoryEdit.optionalRemark')" clearable /></template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="90" fixed="right">
          <template slot-scope="scope">
            <el-button type="danger" size="mini" @click="_removeSelectResourceStoreItem(scope.row.resId)">{{ $t('common.delete') }}</el-button>
          </template>
        </el-table-column>
      </ds-data-table>
    </div>

    <choose-resource-store2 ref="chooseResourceStore2" @setSelectResourceStores="handleSelectResourceStores" />
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { getAssetInventoryDetail, updateAssetInventory, listAssetInventoryWholeDetail } from '@/api/resource/assetInventoryEditApi'
import ChooseResourceStore2 from '@/components/resource/chooseResourceStore2'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'

export default {
  name: 'AssetInventoryEditList',
  components: { ChooseResourceStore2, DsPageHeader, DsToolbar, DsDataTable },
  data() {
    return {
      assetInventoryEditInfo: { aiId: '', shId: '', shName: '', name: '', staffName: '', invTime: '', shopHouses: [], resourceStores: [], state: '2000', remark: '', communityId: '' }
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.assetInventoryEditInfo.aiId = this.$route.query.aiId
    this._queryAssetInventory()
    this.queryAssetInventoryProduct()
  },
  methods: {
    async _queryAssetInventory() {
      try {
        const params = { page: 1, row: 1, aiId: this.assetInventoryEditInfo.aiId, shopId: this.communityId }
        const { data } = await getAssetInventoryDetail(params)
        Object.assign(this.assetInventoryEditInfo, data[0])
      } catch (error) { this.$message.error(this.$t('assetInventoryEdit.fetchError')) }
    },
    async queryAssetInventoryProduct() {
      try {
        const params = { page: 1, row: 100, applyOrderId: this.assetInventoryEditInfo.aiId }
        const { data } = await listAssetInventoryWholeDetail(params)
        data.forEach(item => { this._computeData(item) })
        this.assetInventoryEditInfo.resourceStores = data
      } catch (error) { this.$message.error(this.$t('assetInventoryEdit.fetchProductError')) }
    },
    _computeData(item) {
      item.invProfit = parseInt(item.invQuantity) - parseInt(item.quantity)
      if (item.invProfit < 0) item.invProfit = 0
      item.invLoss = parseInt(item.quantity) - parseInt(item.invQuantity)
      if (item.invLoss < 0) item.invLoss = 0
      item.invProfitMoney = item.invProfit * parseFloat(item.price)
      item.invLossMoney = item.invLoss * parseFloat(item.price)
    },
    _openChooseProductModal() {
      if (!this.assetInventoryEditInfo.shId) { this.$message.warning(this.$t('assetInventoryEdit.selectWarehouseFirst')); return }
      this.$refs.chooseResourceStore2.open({ shId: this.assetInventoryEditInfo.shId, unEditFlag: true })
    },
    handleSelectResourceStores(resourceStores) {
      const oldList = this.assetInventoryEditInfo.resourceStores
      const newItems = resourceStores.filter(newItem => !oldList.some(oldItem => oldItem.resId === newItem.resId))
      this.assetInventoryEditInfo.resourceStores = [...newItems, ...oldList]
    },
    _removeSelectResourceStoreItem(resId) {
      this.assetInventoryEditInfo.resourceStores = this.assetInventoryEditInfo.resourceStores.filter(item => item.resId !== resId)
      this.$refs.chooseResourceStore2.removeSelectItem(resId)
    },
    _changeTimesId(value, index) {
      const times = this.assetInventoryEditInfo.resourceStores[index].times
      const selectedTime = times.find(item => item.timesId === value)
      if (selectedTime) { this.assetInventoryEditInfo.resourceStores[index].selectedStock = selectedTime.stock }
    },
    _getTimesStock(resourceStore) {
      if (!resourceStore.timesId) return "-"
      const selectedTime = resourceStore.times.find(item => item.timesId === resourceStore.timesId)
      return selectedTime ? selectedTime.stock : 0
    },
    async _doAssetInventory() {
      const products = this.assetInventoryEditInfo.resourceStores
      this.assetInventoryEditInfo.state = "2000"
      if (products.length < 1) { this.$message.warning(this.$t('assetInventoryEdit.selectProductsFirst')); return }
      for (const product of products) {
        if (!product.quantity || parseInt(product.quantity) <= 0) { this.$message.warning(this.$t('assetInventoryEdit.fillQty')); return }
        if (!product.timesId) { this.$message.warning(`${product.resName},${this.$t('assetInventoryEdit.selectPrice')}`); return }
      }
      try {
        const { msg } = await updateAssetInventory(this.assetInventoryEditInfo)
        this.$message.success(msg)
        this.$router.go(-1)
      } catch (error) { this.$message.error(error.message || this.$t('assetInventoryEdit.submitError')) }
    },
    _goBack() { this.$router.go(-1) }
  }
}
</script>

<style lang="scss" scoped>
.asset-inventory-edit-container { padding: var(--ds-spacing-lg); background-color: var(--ds-gray-100); min-height: calc(100vh - 60px); }
.ds-panel { background: #fff; border: 1px solid var(--ds-gray-300); border-radius: var(--ds-radius-md); box-shadow: var(--ds-shadow-sm); overflow: hidden; margin-bottom: var(--ds-spacing-lg);
  &--padded { padding: var(--ds-spacing-lg); }
  &__header { display: flex; align-items: center; justify-content: space-between; padding-bottom: var(--ds-spacing-md); margin-bottom: var(--ds-spacing-md); border-bottom: 1px solid var(--ds-gray-200); }
  &__title { font-size: var(--ds-font-size-lg); font-weight: 600; color: var(--ds-gray-800); } }
.ds-info-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: var(--ds-spacing-md) var(--ds-spacing-lg);
  &__item { display: flex; flex-direction: column; gap: 4px; &--wide { grid-column: 1 / -1; } }
  &__label { font-size: var(--ds-font-size-sm); color: var(--ds-gray-500); }
  &__value { font-size: var(--ds-font-size-md); color: var(--ds-gray-800); word-break: break-all; } }
</style>
